import { useState, useEffect } from 'react';
import { supabase, Profile } from '../lib/supabase';
import { User } from '@supabase/supabase-js';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Obtener sesión inicial
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error al obtener perfil:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    email: string,
    password: string,
    tipoPerfil: 'comprador' | 'vendedor',
    profileData?: {
      nombre_completo?: string;
      pueblo?: string;
      telefono?: string;
      direccion?: string;
      tipo_identificacion?: string;
      numero_identificacion?: string;
      url_identificacion?: string;
    }
  ) => {
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) {
        const msg = authError.message || 'Error al crear la cuenta';
        throw new Error(msg);
      }
      if (!authData.user) throw new Error('No se pudo crear el usuario');

      // Auto-confirmamos el email en la Edge Function create-profile,
      // así que nunca necesitamos mostrar la pantalla de "confirma tu email"
      const needsEmailConfirmation = false;

      // Crear perfil usando Edge Function (bypasea RLS de forma segura)
      // La Edge Function también auto-confirma el email y envía bienvenida con Resend
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const profileRes = await fetch(`${supabaseUrl}/functions/v1/create-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: authData.user.id,
          tipo_perfil: tipoPerfil,
          nombre_completo: profileData?.nombre_completo || null,
          pueblo: profileData?.pueblo || null,
          telefono: profileData?.telefono || null,
          direccion: profileData?.direccion || null,
          tipo_identificacion: profileData?.tipo_identificacion || null,
          numero_identificacion: profileData?.numero_identificacion || null,
          url_identificacion: profileData?.url_identificacion || null,
        }),
      });

      if (!profileRes.ok) {
        const errBody = await profileRes.json().catch(() => ({}));
        const msg =
          (errBody as { error?: string }).error ||
          'Error al guardar el perfil en la base de datos';
        throw new Error(msg);
      }

      // Si no hay sesión activa (email no confirmado aún en Supabase),
      // hacemos login automático ahora que la Edge Function ya confirmó el email
      let finalUser = authData.user;
      if (!authData.session) {
        const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (!loginError && loginData.user) {
          finalUser = loginData.user;
        }
      }

      return {
        success: true,
        user: finalUser,
        needsEmailConfirmation,
      };
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
          ? String((error as any).message)
          : 'Error desconocido al registrarse';

      console.error('Error en registro:', message);
      return { success: false, error: message, needsEmailConfirmation: false };
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { success: true, user: data.user };
    } catch (error: any) {
      console.error('Error en login:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { success: true };
    } catch (error: any) {
      console.error('Error en logout:', error);
      return { success: false, error: error.message };
    }
  };

  return {
    user,
    profile,
    loading,
    register,
    login,
    logout,
  };
};